import { useEffect, useRef, useState } from 'react';
import {
  socketService,
  SessionState,
  TokenMovedEvent,
  TokenCreatedEvent,
  TokenDeletedEvent,
  TokensDeletedEvent,
  TokenUpdatedEvent,
  ParticipantJoinedEvent,
  ParticipantLeftEvent,
  ErrorEvent,
  BarrierCreatedEvent,
  BarrierDeletedEvent,
  BarrierUpdatedEvent,
  MapVisionUpdatedEvent,
  FogRevealedEvent,
  FogHiddenEvent,
  FogSetAllDoneEvent,
  ChatMessageSentEvent,
  ChatHistoryEvent,
  DiceRolledEvent,
  MapSwitchedEvent,
  MapCreatedEvent,
  MapUpdatedEvent,
  MapDeletedEvent,
  InitiativeUpdatedEvent,
  InitiativeClearedEvent,
  RoleChangedEvent,
  GMHandoffOfferedEvent,
  GMHandoffExpiredEvent,
  RulerUpdatedEvent,
  PingPlacedEvent,
  CharacterHpUpdatedEvent,
  CharacterClassificationUpdatedEvent,
  LightSourceCreatedEvent,
  LightSourceUpdatedEvent,
  LightSourceDeletedEvent,
} from '../services/socket';
import { useVTTStore } from '../store';
import { useInitiativeStore } from '../store/initiativeStore';

export interface UseWebSocketOptions {
  token: string;
  sessionId: string;
  currentUserId: string; // To filter out our own events
  /**
   * Live role for this client (tracks GM handoffs in real time). Used to
   * gate GM-only client behaviors — currently only the "auto-switch to a
   * freshly created map" convenience. Server still enforces role on
   * every privileged event; this just avoids players firing rejected
   * map_switch emits.
   */
  role?: 'gm' | 'player' | 'observer';
  onConnected?: () => void;
  onDisconnected?: () => void;
  onError?: (error: string) => void;
  /**
   * Called when the server broadcasts a role change that affects this
   * client (the GM seat was passed/taken). Receives the new role for the
   * local user. App.tsx uses this to update its `role` state without a
   * full session refresh.
   */
  onRoleChanged?: (newRole: 'gm' | 'player') => void;
  /**
   * Called when a token's character data may have gone stale relative to
   * the platform (a new token spawned with a characterId we haven't
   * cached, or an existing token was re-linked to a different
   * characterId). App.tsx triggers a re-fetch via characterService.
   */
  onCharacterNeedsRefresh?: (
    characterId: string,
    previousCharacterId: string | null
  ) => void;
}

export interface WebSocketStatus {
  connected: boolean;
  connecting: boolean;
  error: string | null;
  authFailed: boolean; // Flag for authentication failures
  participants: Array<{
    userId: string;
    displayName: string;
    role: string;
  }>;
}

/**
 * React hook for managing WebSocket connection and events
 */
export function useWebSocket(options: UseWebSocketOptions): WebSocketStatus {
  const { token, sessionId, currentUserId, role, onConnected, onDisconnected, onError, onRoleChanged, onCharacterNeedsRefresh } = options;

  const [status, setStatus] = useState<WebSocketStatus>({
    connected: false,
    connecting: false,
    error: null,
    authFailed: false,
    participants: [],
  });

  const { moveToken, setTokens, upsertToken, removeToken, removeTokens, setMaps, setActiveMapId, addMap, updateMap, removeMap, addBarrier, removeBarrier, setBarriers, replaceBarrier, updateBarrier, setLosEnabled, setShowBarriers, setRenderMode, setFog, revealFogCells, hideFogCells, addMessage, setMessages, setGmHandoffOffer, upsertRemoteRuler, clearRemoteRuler, addPing, setLightSources, addLightSource, removeLightSource, updateLightSourceInStore, setLightingEnabled } = useVTTStore();
  const setInitiative = useInitiativeStore((s) => s.setInitiative);
  const clearInitiativeStore = useInitiativeStore((s) => s.clearInitiative);
  const participantsRef = useRef<Array<{ userId: string; displayName: string; role: string }>>([]);
  // #492: the store keeps a single global `fog` Map for the active map only, and
  // map_switched carries no fog payload. Cache every map's revealed cells from the
  // last session_state so a map switch can deterministically re-hydrate the fog for
  // the map being switched to (clear-before-apply) instead of leaving the previous
  // map's cells stranded on the new map.
  const fogByMapRef = useRef<Map<string, Map<string, boolean>>>(new Map());

  // Build a fresh fog Map for one map's revealedData, keeping only revealed cells.
  const buildFogMap = (revealedData: unknown): Map<string, boolean> => {
    const m = new Map<string, boolean>();
    if (revealedData && typeof revealedData === 'object') {
      for (const [key, value] of Object.entries(revealedData as Record<string, unknown>)) {
        if (value === true) m.set(key, true);
      }
    }
    return m;
  };

  useEffect(() => {
    // Don't connect if no token provided
    if (!token || !sessionId) {
      return;
    }

    let mounted = true;

    async function connect() {
      try {
        setStatus((prev) => ({ ...prev, connecting: true, error: null }));

        // Connect to WebSocket
        await socketService.connect(token);

        if (!mounted) return;

        // Register event listeners
        setupEventListeners();

        // Join session
        socketService.joinSession(sessionId);

      } catch (error) {
        if (!mounted) return;

        const errorMessage = error instanceof Error ? error.message : 'Connection failed';
        const isAuthError = errorMessage.includes('Authentication failed') ||
                           errorMessage.includes('Invalid token') ||
                           errorMessage.includes('Token expired');

        setStatus((prev) => ({
          ...prev,
          connecting: false,
          connected: false,
          error: errorMessage,
          authFailed: isAuthError,
        }));

        onError?.(errorMessage);
      }
    }

    function setupEventListeners() {
      // Session state (full hydration)
      socketService.on<SessionState>('session_state', (state) => {
        console.log('[WebSocket] Received session state', state);

        // Update participants list
        participantsRef.current = state.participants;
        setStatus((prev) => ({
          ...prev,
          connected: true,
          connecting: false,
          participants: state.participants,
        }));

        // Hydrate maps (Phase 6: multi-map support). The server is the
        // source of truth, including the empty case: a brand-new
        // campaign tabletop legitimately has zero maps, and we clear the
        // store to match. That's now safe — App gates the VTTCanvas mount
        // on `hasActiveMap`, so when activeMapId goes null the canvas
        // unmounts cleanly (it's no longer rendered) instead of hitting
        // its `if (!map) return`-above-hooks path mid-render. With no
        // active map, App shows <BlankGrid> and the GM creates one.
        const maps = state.maps.map((m) => ({
          id: m.id,
          sessionId: m.sessionId,
          name: m.name,
          width: m.width,
          height: m.height,
          gridSize: m.gridSize,
          gridColor: m.gridColor,
          gridOpacity: m.gridOpacity,
          showGrid: m.showGrid,
          gridOffsetX: m.gridOffsetX,
          gridOffsetY: m.gridOffsetY,
          backgroundOffsetX: m.backgroundOffsetX,
          backgroundOffsetY: m.backgroundOffsetY,
          backgroundScaleX: m.backgroundScaleX,
          backgroundScaleY: m.backgroundScaleY,
          backgroundUrl: m.backgroundUrl || undefined,
          backgroundColor: '#1a1a1a',
          isActive: m.isActive,
          losEnabled: m.losEnabled,
          showBarriers: m.showBarriers,
          lightingEnabled: m.lightingEnabled ?? false,
          renderMode: (m.renderMode as 'dimmed' | 'darkness') ?? 'darkness',
        }));

        console.log('[WebSocket] Hydrating maps:', maps);
        setMaps(maps);

        const activeMap = maps.find((m) => m.isActive);
        console.log('[WebSocket] Setting active map:', activeMap?.id ?? null);
        setActiveMapId(activeMap ? activeMap.id : null);

        // Hydrate tokens
        const tokens = state.tokens.map((t) => ({
          id: t.id,
          mapId: t.mapId, // Phase 6: map association
          name: t.name,
          x: t.x,
          y: t.y,
          width: t.width,
          height: t.height,
          color: t.color || '#FF5733',
          imageUrl: t.imageUrl || undefined,
          ownerUserId: t.ownerUserId || null,
          visionRadius: t.visionRadius,
          characterId: t.characterId ?? null,
          // Two settings only: 'gm_only' (never shown to players) or 'visible'
          // (obeys fog/LOS). Normalize the legacy 'hidden' value and any
          // absent flag to 'visible'.
          visibility: (t.visibility === 'gm_only' ? 'gm_only' : 'visible') as 'visible' | 'gm_only',
          lightRadius: t.lightRadius ?? null,
          torchActive: t.torchActive ?? false,
          lowLightVision: t.lowLightVision ?? false,
          darkvision: t.darkvision ?? false,
          // Mob token per-instance stats — null for linked tokens.
          // Explicitly rehydrated on every reconnect to close the hydration gap.
          instanceStats: (t as any).instanceStats ?? null,
        }));

        console.log('[WebSocket] Hydrating tokens:', tokens);
        setTokens(tokens);
        console.log('[WebSocket] Tokens set in store');

        // Hydrate barriers
        const barriers = state.barriers.map((b) => ({
          id: b.id,
          mapId: b.mapId,
          points: b.geometry.points,
          closed: (b.geometry as { closed?: boolean }).closed,
        }));
        console.log('[WebSocket] Hydrating barriers:', barriers);
        setBarriers(barriers);

        // Hydrate standalone light sources
        setLightSources(
          (state.lightSources ?? []).map((ls) => ({
            id: ls.id,
            mapId: ls.mapId,
            x: ls.x,
            y: ls.y,
            radius: ls.radius,
            brightRadius: ls.brightRadius,
            dimRadius: ls.dimRadius,
            color: ls.color,
            falloff: ls.falloff,
          }))
        );

        // Hydrate initiative tracker.
        // Spec iter-4: `session_state.initiative` is always present —
        // the empty default `{ entries:[], currentIndex:0, round:1 }` is
        // sent by the server when no row exists. Read unconditionally.
        if (state.initiative) {
          setInitiative({
            mapId: state.initiative.mapId,
            entries: state.initiative.entries,
            currentIndex: state.initiative.currentIndex,
            round: state.initiative.round,
          });
        } else {
          // Defensive: pre-rollout server may not send the field. Treat
          // as the empty default so the panel still renders.
          clearInitiativeStore();
        }

        // Hydrate fog of war.
        // #492: session_state.fog carries one entry PER MAP. The store holds a
        // single global `fog` Map for the ACTIVE map, so we must (a) cache every
        // map's fog for later switches, and (b) load ONLY the active map's fog —
        // and always replace wholesale (clear-before-hydrate), even when the
        // active map has no fog rows, so a refresh deterministically reproduces
        // the saved state instead of merging every map's cells or leaving stale
        // cells from before the refresh.
        fogByMapRef.current = new Map();
        (state.fog ?? []).forEach((f) => {
          fogByMapRef.current.set(f.mapId, buildFogMap(f.revealedData));
        });
        const activeFog =
          (activeMap && fogByMapRef.current.get(activeMap.id)) ?? new Map<string, boolean>();
        console.log('[WebSocket] Hydrating fog for active map:', activeMap?.id ?? null, activeFog.size, 'revealed cells');
        setFog(activeFog);

        onConnected?.();
      });

      // Token moved
      socketService.on<TokenMovedEvent>('token_moved', (data) => {
        // Skip our own moves - we already handled them locally
        // TODO: Re-enable this after server is deployed with waypoint support
        // if (data.userId === currentUserId) {
        //   console.log('[WebSocket] Skipping own token move');
        //   return;
        // }

        // For now, don't apply server events if they're our own (we handle locally)
        if (data.userId === currentUserId) {
          console.log('[WebSocket] Skipping server echo of own move');
          return;
        }

        // Phase 3c: Pass instant flag to store so animation system can use it
        moveToken(data.tokenId, data.to.x, data.to.y, data.waypoints, data.instant);
      });

      // Ruler updated — another participant is (or just stopped) measuring.
      // Transient relay: we render it ephemerally and drop it when active
      // goes false. Skip our own echo (the server excludes the sender, but
      // guard anyway — our local ruler is already on screen).
      socketService.on<RulerUpdatedEvent>('ruler_updated', (data) => {
        if (data.userId === currentUserId) return;
        upsertRemoteRuler({
          userId: data.userId,
          displayName: data.displayName,
          mapId: data.mapId,
          start: data.start,
          end: data.end,
          active: data.active,
          shape: data.shape,
        });
      });

      // Ping placed — a participant dropped a transient "look here" pulse via
      // left-click-and-hold. The server broadcasts to the whole room INCLUDING
      // the sender (io.to). We skip our own echo: the local long-press already
      // added the ping to the store, and re-adding would reset its TTL clock.
      // Distinct from the `ping`/`pong` heartbeat — this is the game ping.
      socketService.on<PingPlacedEvent>('ping_placed', (data) => {
        if (data.userId === currentUserId) return;
        addPing({
          id: `${data.userId}:${Date.now()}`,
          userId: data.userId,
          mapId: data.mapId,
          x: data.x,
          y: data.y,
          createdAt: performance.now(),
        });
      });

      // Token created (GM spawned a token — e.g. dragged a character from the
      // party roster onto the map). Server broadcasts the canonical row.
      socketService.on<TokenCreatedEvent>('token_created', (data) => {
        console.log('[WebSocket] token_created received', {
          tokenId: data.token.id,
          name: data.token.name,
          mapId: data.token.mapId,
          characterId: data.token.characterId,
        });
        upsertToken({
          id: data.token.id,
          mapId: data.token.mapId,
          name: data.token.name,
          x: data.token.x,
          y: data.token.y,
          width: data.token.width,
          height: data.token.height,
          color: data.token.color || '#3b82f6',
          imageUrl: data.token.imageUrl || undefined,
          ownerUserId: data.token.ownerUserId ?? null,
          visionRadius: data.token.visionRadius,
          characterId: data.token.characterId ?? null,
          // Two settings only: 'gm_only' (never shown to players) or 'visible'
          // (a normal token that obeys fog/LOS). Normalize the legacy 'hidden'
          // value and any absent flag to 'visible'.
          visibility: data.token.visibility === 'gm_only' ? 'gm_only' : 'visible',
          // Mob token per-instance stats — null for linked tokens.
          instanceStats: (data.token as any).instanceStats ?? null,
        });
        // Newly-spawned tokens often reference a character that
        // wasn't part of the initial fetchSessionCharacters cache (NPCs
        // created mid-session, freshly-linked PCs). Without this prompt,
        // the HUD (HP bar, abilities, init mod) stays empty until reload.
        if (data.token.characterId) {
          onCharacterNeedsRefresh?.(data.token.characterId, null);
        }
      });

      // Token deleted (GM removed it via the right-click context menu,
      // or an owner deleted their own token). Server enforces the
      // permission gate on the way in; here we just drop the store row
      // so the canvas re-renders without it.
      socketService.on<TokenDeletedEvent>('token_deleted', (data) => {
        removeToken(data.tokenId);
      });

      // Bulk delete broadcast — emitted in response to `tokens_delete`
      // (multi-select Delete on canvas). `tokenIds` lists everything
      // that was actually removed server-side; some IDs from the
      // original request may be missing if they failed the
      // permission check. Drop the whole listed set in one go.
      socketService.on<TokensDeletedEvent>('tokens_deleted', (data) => {
        removeTokens(data.tokenIds);
      });

      // Token updated — merge the incoming updates onto the existing
      // store row. Handles name edits, visibility, and GM characterId
      // repoints (disconnect-from-mob). Uses getState() to avoid
      // capturing a stale token snapshot in the closure.
      socketService.on<TokenUpdatedEvent>('token_updated', (data) => {
        const token = useVTTStore.getState().tokens.find((t) => t.id === data.tokenId);
        if (!token) return;
        const previousCharacterId: string | null = token.characterId ?? null;
        upsertToken({
          ...token,
          ...(data.updates.name !== undefined ? { name: data.updates.name } : {}),
          ...(data.updates.color !== undefined ? { color: data.updates.color } : {}),
          ...(data.updates.visionRadius !== undefined ? { visionRadius: data.updates.visionRadius } : {}),
          ...(data.updates.characterId !== undefined ? { characterId: data.updates.characterId } : {}),
          ...(data.updates.visibility !== undefined ? { visibility: data.updates.visibility === 'gm_only' ? 'gm_only' as const : 'visible' as const } : {}),
          ...(data.updates.hpBarVisibleToPlayers !== undefined ? { hpBarVisibleToPlayers: data.updates.hpBarVisibleToPlayers } : {}),
          // These were broadcast by the server but never applied here, so they
          // only ever showed on the client that made the change — statuses is
          // issue #139; rotation/size had the same cross-client desync.
          ...(data.updates.statuses !== undefined ? { statuses: data.updates.statuses } : {}),
          ...(data.updates.rotation !== undefined ? { rotation: data.updates.rotation } : {}),
          ...(data.updates.width !== undefined ? { width: data.updates.width } : {}),
          ...(data.updates.height !== undefined ? { height: data.updates.height } : {}),
          ...(data.updates.lightRadius !== undefined ? { lightRadius: data.updates.lightRadius } : {}),
          ...(data.updates.torchActive !== undefined ? { torchActive: data.updates.torchActive } : {}),
          ...(data.updates.lowLightVision !== undefined ? { lowLightVision: data.updates.lowLightVision } : {}),
          ...(data.updates.darkvision !== undefined ? { darkvision: data.updates.darkvision } : {}),
          // Mob token: per-instance combat stats (HP/statuses). Null clears instanceStats (makes token linked).
          ...(data.updates.instanceStats !== undefined ? { instanceStats: data.updates.instanceStats } : {}),
        });
        // Re-link (characterId changed): force a refresh so the HUD shows
        // the new character's stats rather than the cached old ones.
        if (
          data.updates.characterId !== undefined &&
          data.updates.characterId !== previousCharacterId &&
          data.updates.characterId
        ) {
          onCharacterNeedsRefresh?.(data.updates.characterId, previousCharacterId);
        }
      });

      // Character HP changed on the sheet (issue #135) — refetch that character
      // so its HP bar reflects the new value for everyone in the session.
      socketService.on<CharacterHpUpdatedEvent>('character_hp_updated', (data) => {
        onCharacterNeedsRefresh?.(data.characterId, null);
      });

      // Token classification changed (VTT #163) — refetch that character so the
      // right-sidebar bank re-buckets (player/friendly/enemy) and the canvas
      // enemy outline updates for everyone in the session.
      socketService.on<CharacterClassificationUpdatedEvent>(
        'character_classification_updated',
        (data) => {
          onCharacterNeedsRefresh?.(data.characterId, null);
        }
      );

      // Participant joined
      socketService.on<ParticipantJoinedEvent>('participant_joined', (data) => {
        console.log('[WebSocket] Participant joined', data);
        participantsRef.current = [...participantsRef.current, data];
        setStatus((prev) => ({
          ...prev,
          participants: participantsRef.current,
        }));
      });

      // Participant left
      socketService.on<ParticipantLeftEvent>('participant_left', (data) => {
        console.log('[WebSocket] Participant left', data);
        participantsRef.current = participantsRef.current.filter(
          (p) => p.userId !== data.userId
        );
        setStatus((prev) => ({
          ...prev,
          participants: participantsRef.current,
        }));
        // Drop any ruler that participant was broadcasting so it doesn't
        // linger after they disconnect mid-measurement.
        clearRemoteRuler(data.userId);
      });

      // Participants updated (authoritative list of connected users)
      socketService.on<{ participants: Array<{ userId: string; displayName: string; role: string }> }>(
        'participants_updated',
        (data) => {
          console.log('[WebSocket] Participants updated', data);
          participantsRef.current = data.participants;
          setStatus((prev) => ({
            ...prev,
            participants: data.participants,
          }));
        }
      );

      // Error events
      socketService.on<ErrorEvent>('error', (error) => {
        console.error('[WebSocket] Error event', error);

        // Surface initiative-specific failures in the panel. Without this an
        // invalid_data rejection (e.g. a token the server won't accept) was
        // only console-logged, so the GM saw the modal close and nothing else.
        if (error.originalEvent?.startsWith('initiative_')) {
          useInitiativeStore.getState().setError(error.message);
        }

        // Only treat the error as session-fatal (kick to LandingPage)
        // when the failing operation is the join itself — the server's
        // ErrorEvent type doesn't distinguish "session missing" from
        // "map missing", so the originalEvent is the only reliable
        // signal. Operational not_found / permission_denied (e.g. map
        // not found on token_create, ownership check on token_move)
        // should surface to the caller but NOT tear down the session.
        const isFatal = error.originalEvent === 'join_session';

        if (isFatal) {
          setStatus((prev) => ({
            ...prev,
            authFailed: true,
            error: error.message,
          }));
        }

        onError?.(error.message);
      });

      // Barrier created.
      // The GM who drew the barrier already added an optimistic copy locally
      // (VTTCanvas completeBarrier → addBarrier). Without dedup, this handler
      // would add the server-confirmed copy alongside it — two barriers with
      // the same points, different IDs, both rendering. Find any optimistic
      // match by mapId + point-sequence and swap its id for the server's;
      // otherwise (peers in the session, or no optimistic add) just append.
      socketService.on<BarrierCreatedEvent>('barrier_created', (data) => {
        console.log('[WebSocket] Barrier created', data);
        const incoming = {
          id: data.barrier.id,
          mapId: data.barrier.mapId,
          points: data.barrier.points,
          closed: data.barrier.closed,
        };
        const incomingKey = JSON.stringify(incoming.points);
        const existing = useVTTStore.getState().barriers.find(
          (b) =>
            b.mapId === incoming.mapId &&
            b.id !== incoming.id &&
            JSON.stringify(b.points) === incomingKey
        );
        if (existing) {
          replaceBarrier(existing.id, incoming);
        } else {
          addBarrier(incoming);
        }
      });

      // Barrier deleted
      socketService.on<BarrierDeletedEvent>('barrier_deleted', (data) => {
        console.log('[WebSocket] Barrier deleted', data);
        removeBarrier(data.barrierId);
      });

      // Barrier flag-bit update (blocksVision / blocksMovement). GM may
      // have applied this optimistically already; updateBarrier is
      // idempotent on the same values, so re-applying from the broadcast
      // is safe.
      socketService.on<BarrierUpdatedEvent>('barrier_updated', (data) => {
        console.log('[WebSocket] Barrier updated', data);
        updateBarrier(data.barrier.id, {
          blocksVision: data.barrier.blocksVision,
          blocksMovement: data.barrier.blocksMovement,
        });
      });

      // #492: the store holds a single global `fog` map for the ACTIVE map only
      // (per-map fog is realized by hydrating the active map's fog on switch). Each
      // fog broadcast therefore (1) updates the per-map cache for data.mapId, and
      // (2) applies to the live store ONLY when data.mapId is the active map.
      // Without the active-map guard a fog op on map A (Hide all / All visible, or
      // a brush stroke) overwrote whatever fog the local client had loaded — which
      // is why the bulk buttons appeared to affect every map.

      // Fog revealed
      socketService.on<FogRevealedEvent>('fog_revealed', (data) => {
        console.log('[WebSocket] Fog revealed', data);
        // #492: mirror into the per-map cache so a later switch back is coherent.
        const cached = fogByMapRef.current.get(data.mapId) ?? new Map<string, boolean>();
        data.cells.forEach((c) => cached.set(`${c.x},${c.y}`, true));
        fogByMapRef.current.set(data.mapId, cached);
        if (data.mapId !== useVTTStore.getState().activeMapId) return;
        revealFogCells(data.cells);
      });

      // Fog hidden
      socketService.on<FogHiddenEvent>('fog_hidden', (data) => {
        console.log('[WebSocket] Fog hidden', data);
        // #492: mirror into the per-map cache so a later switch back is coherent.
        const cached = fogByMapRef.current.get(data.mapId) ?? new Map<string, boolean>();
        data.cells.forEach((c) => cached.delete(`${c.x},${c.y}`));
        fogByMapRef.current.set(data.mapId, cached);
        if (data.mapId !== useVTTStore.getState().activeMapId) return;
        hideFogCells(data.cells);
      });

      // Bulk fog set (hide-all / clear-all) — replace fog state wholesale
      // from the server's authoritative revealed-cell map.
      socketService.on<FogSetAllDoneEvent>('fog_set_all_done', (data) => {
        console.log('[WebSocket] Fog set all', { mapId: data.mapId, revealed: data.revealed });
        const next = new Map<string, boolean>();
        for (const key of Object.keys(data.fog)) {
          if (data.fog[key]) next.set(key, true);
        }
        // #492: always refresh the per-map cache, but only apply to the live store
        // when this is the map the client is actually viewing — the bulk buttons
        // must not overwrite the fog of a map the client isn't looking at.
        fogByMapRef.current.set(data.mapId, next);
        if (data.mapId !== useVTTStore.getState().activeMapId) return;
        setFog(next);
      });

      // Map vision updated
      socketService.on<MapVisionUpdatedEvent>('map_vision_updated', (data) => {
        console.log('[WebSocket] Map vision updated', data);
        if (data.losEnabled !== undefined) {
          setLosEnabled(data.losEnabled);
        }
        if (data.showBarriers !== undefined) {
          setShowBarriers(data.showBarriers);
        }
        if (data.lightingEnabled !== undefined) {
          setLightingEnabled(data.lightingEnabled);
        }
        if (data.renderMode !== undefined) {
          setRenderMode(data.renderMode);
        }
      });

      // Dynamic lighting — standalone light source events
      socketService.on<LightSourceCreatedEvent>('light_source_created', (data) => {
        console.log('[WebSocket] Light source created', data);
        addLightSource({
          id: data.lightSource.id,
          mapId: data.lightSource.mapId,
          x: data.lightSource.x,
          y: data.lightSource.y,
          radius: data.lightSource.radius,
          brightRadius: data.lightSource.brightRadius,
          dimRadius: data.lightSource.dimRadius,
          color: data.lightSource.color,
          falloff: data.lightSource.falloff,
        });
      });

      socketService.on<LightSourceUpdatedEvent>('light_source_updated', (data) => {
        console.log('[WebSocket] Light source updated', data);
        updateLightSourceInStore(data.lightSourceId, {
          radius: data.radius,
          x: data.x,
          y: data.y,
          brightRadius: data.brightRadius,
          dimRadius: data.dimRadius,
          color: data.color,
          falloff: data.falloff,
        });
      });

      socketService.on<LightSourceDeletedEvent>('light_source_deleted', (data) => {
        console.log('[WebSocket] Light source deleted', data);
        removeLightSource(data.lightSourceId);
      });

      // Chat & dice history (replayed on session-join from EventLog).
      // Fires once per join, before any live `chat_message_sent` /
      // `dice_rolled` events. We replace the feed wholesale instead of
      // appending so a reconnect doesn't double up prior messages.
      socketService.on<ChatHistoryEvent>('chat_history', (data) => {
        console.log('[WebSocket] Chat history received', { count: data.entries.length });
        const replay = data.entries.map((e) => {
          if (e.kind === 'chat_message') {
            return {
              messageId: e.messageId,
              userId: e.userId,
              displayName: e.displayName,
              message: e.message,
              timestamp: e.timestamp,
              isWhisper: e.isWhisper,
            };
          }
          return {
            rollId: e.rollId,
            userId: e.userId,
            displayName: e.displayName,
            label: e.label,
            notation: e.notation,
            total: e.total,
            output: e.output,
            rolls: [],
            timestamp: e.timestamp,
            isWhisper: e.isWhisper,
          };
        });
        setMessages(replay);
      });

      // Chat message sent
      socketService.on<ChatMessageSentEvent>('chat_message_sent', (data) => {
        console.log('[WebSocket] Chat message received', data);
        addMessage({
          messageId: data.messageId,
          userId: data.userId,
          displayName: data.displayName,
          message: data.message,
          timestamp: data.timestamp,
          isWhisper: data.isWhisper,
        });
      });

      // Dice rolled
      socketService.on<DiceRolledEvent>('dice_rolled', (data) => {
        console.log('[WebSocket] Dice roll received', data);
        addMessage({
          rollId: data.rollId,
          userId: data.userId,
          displayName: data.displayName,
          label: data.label,
          notation: data.notation,
          total: data.total,
          output: data.output,
          rolls: data.rolls,
          timestamp: data.timestamp,
          isWhisper: data.isWhisper,
        });
      });

      // Phase 6: Map switched
      socketService.on<MapSwitchedEvent>('map_switched', (data) => {
        console.log('[WebSocket] Map switched', data);
        // Update the map in the store
        updateMap(data.map.id, {
          isActive: data.map.isActive,
          // Update all other fields too
          gridColor: data.map.gridColor,
          gridOpacity: data.map.gridOpacity,
          showGrid: data.map.showGrid,
          gridOffsetX: data.map.gridOffsetX,
          gridOffsetY: data.map.gridOffsetY,
          // Background transform fields — a switched-to map must carry its
          // offset/scale/fit so the canvas paints the image exactly as the GM
          // configured it, not the store defaults (same desync class as #159/
          // #160, applied to the switch path).
          backgroundOffsetX: data.map.backgroundOffsetX,
          backgroundOffsetY: data.map.backgroundOffsetY,
          backgroundScaleX: data.map.backgroundScaleX,
          backgroundScaleY: data.map.backgroundScaleY,
          backgroundFit: data.map.backgroundFit ?? undefined,
          backgroundUrl: data.map.backgroundUrl || undefined,
          losEnabled: data.map.losEnabled,
          showBarriers: data.map.showBarriers,
          renderMode: (data.map.renderMode as 'dimmed' | 'darkness') ?? 'darkness',
        });
        // Set as active map
        setActiveMapId(data.map.id);
        // #492: map_switched carries no fog payload, and the store's `fog` Map is
        // global to the active map — so re-hydrate it from the per-map cache
        // (clear-before-apply). Without this the previous map's revealed/hidden
        // cells stay stranded on the newly-opened map. Falls back to empty when
        // the map has no cached fog (either genuinely all-hidden or not yet seen),
        // which is still correct: a stale foreign map's fog is never left behind.
        setFog(fogByMapRef.current.get(data.map.id) ?? new Map<string, boolean>());
      });

      // Phase 6: Map created
      socketService.on<MapCreatedEvent>('map_created', (data) => {
        console.log('[WebSocket] Map created', data);
        addMap({
          id: data.map.id,
          sessionId: data.map.sessionId,
          name: data.map.name,
          width: data.map.width,
          height: data.map.height,
          gridSize: data.map.gridSize,
          gridColor: data.map.gridColor,
          gridOpacity: data.map.gridOpacity,
          showGrid: data.map.showGrid,
          gridOffsetX: data.map.gridOffsetX,
          gridOffsetY: data.map.gridOffsetY,
          backgroundOffsetX: data.map.backgroundOffsetX,
          backgroundOffsetY: data.map.backgroundOffsetY,
          backgroundScaleX: data.map.backgroundScaleX,
          backgroundScaleY: data.map.backgroundScaleY,
          backgroundFit: data.map.backgroundFit ?? undefined,
          backgroundUrl: data.map.backgroundUrl || undefined,
          backgroundColor: '#1a1a1a',
          isActive: data.map.isActive,
          losEnabled: data.map.losEnabled,
          showBarriers: data.map.showBarriers,
          renderMode: (data.map.renderMode as 'dimmed' | 'darkness') ?? 'darkness',
        });
        // Auto-activate the new map when the GM has none selected yet.
        // The sidebar dropdown's `value={activeMapId || ''}` falls through
        // to the browser default-selecting the first <option>, so the new
        // map LOOKS picked but `activeMapId` is still null — canvas stays
        // on BlankGrid and the edit panel never renders. Firing map_switch
        // here moves the server-side `isActive` over and broadcasts
        // map_switched, which sets activeMapId. GM-only so players don't
        // hijack the table; the server also gates map_switch on role.
        if (role === 'gm' && useVTTStore.getState().activeMapId == null) {
          try {
            socketService.emitMapSwitch(data.map.id);
          } catch (err) {
            console.error('[useWebSocket] auto map_switch emit failed', err);
          }
        }
      });

      // Phase 6: Map updated
      socketService.on<MapUpdatedEvent>('map_updated', (data) => {
        console.log('[WebSocket] Map updated', data);
        updateMap(data.map.id, {
          name: data.map.name,
          width: data.map.width,
          height: data.map.height,
          gridSize: data.map.gridSize,
          gridColor: data.map.gridColor,
          gridOpacity: data.map.gridOpacity,
          showGrid: data.map.showGrid,
          gridOffsetX: data.map.gridOffsetX,
          gridOffsetY: data.map.gridOffsetY,
          backgroundOffsetX: data.map.backgroundOffsetX,
          backgroundOffsetY: data.map.backgroundOffsetY,
          backgroundScaleX: data.map.backgroundScaleX,
          backgroundScaleY: data.map.backgroundScaleY,
          // backgroundFit drives whether applyBackgroundTransform stretches
          // (default) or letterboxes the image — and it changes how offset/
          // scale are interpreted. Dropping it here is what left the player
          // view stretching with "stretch to fill" off (#159) and rendering
          // a stale image transform vs the GM (#160). null → undefined so the
          // store default ('stretch') only ever applies when truly unset.
          backgroundFit: data.map.backgroundFit ?? undefined,
          backgroundUrl: data.map.backgroundUrl || undefined,
          isActive: data.map.isActive,
          losEnabled: data.map.losEnabled,
          showBarriers: data.map.showBarriers,
          renderMode: (data.map.renderMode as 'dimmed' | 'darkness') ?? 'darkness',
        });
      });

      // Phase 6: Map deleted
      socketService.on<MapDeletedEvent>('map_deleted', (data) => {
        console.log('[WebSocket] Map deleted', data);
        removeMap(data.mapId);
      });

      // Pong (response to ping)
      socketService.on('pong', (data: { timestamp: string }) => {
        console.log('[WebSocket] Pong received', data);
      });

      // Initiative updated (broadcast to all session participants).
      socketService.on<InitiativeUpdatedEvent>('initiative_updated', (data) => {
        console.log('[WebSocket] Initiative updated', data);
        setInitiative({
          mapId: data.mapId,
          entries: data.entries,
          currentIndex: data.currentIndex,
          round: data.round,
        });
      });

      // Initiative cleared (broadcast to all session participants).
      socketService.on<InitiativeClearedEvent>('initiative_cleared', (data) => {
        console.log('[WebSocket] Initiative cleared', data);
        clearInitiativeStore();
      });

      // GM role transfer — either a live pass+accept swap (both oldGM
      // and newGM present) or an offline-rescue take (oldGM may be null).
      // Update the participant list and tell App.tsx if this client's
      // role changed. Any pending handoff offer on this client is no
      // longer actionable, so clear it.
      socketService.on<RoleChangedEvent>('role_changed', (data) => {
        console.log('[WebSocket] Role changed', data);
        participantsRef.current = data.participants;
        setStatus((prev) => ({
          ...prev,
          participants: data.participants,
        }));
        setGmHandoffOffer(null);
        if (data.newGM.userId === currentUserId) {
          onRoleChanged?.('gm');
        } else if (data.oldGM && data.oldGM.userId === currentUserId) {
          onRoleChanged?.('player');
        }
      });

      // GM handoff — offer landed on this client (we're the target).
      // Store it so the GMHandoffOffer toast renders with accept/decline.
      socketService.on<GMHandoffOfferedEvent>('gm_handoff_offered', (data) => {
        console.log('[WebSocket] GM handoff offered', data);
        if (data.targetUserId !== currentUserId) return;
        setGmHandoffOffer({
          fromUserId: data.fromUserId,
          fromDisplayName: data.fromDisplayName,
          expiresAt: data.expiresAt,
        });
      });

      // GM handoff offer expired (server TTL) — either party may receive
      // this. If we were the target with an open offer, dismiss the toast.
      socketService.on<GMHandoffExpiredEvent>('gm_handoff_expired', (data) => {
        console.log('[WebSocket] GM handoff expired', data);
        if (data.targetUserId === currentUserId) {
          setGmHandoffOffer(null);
        }
      });

      // GM handoff declined — the target rejected the offer. Server
      // emits to the offering GM. We use the same store slot so the
      // dismissal also clears stale state on whichever side held it.
      socketService.on('gm_handoff_declined', () => {
        console.log('[WebSocket] GM handoff declined');
        setGmHandoffOffer(null);
      });
    }

    connect();

    // Cleanup
    return () => {
      mounted = false;
      socketService.removeAllListeners('session_state');
      socketService.removeAllListeners('token_moved');
      socketService.removeAllListeners('ruler_updated');
      socketService.removeAllListeners('ping_placed');
      socketService.removeAllListeners('token_created');
      socketService.removeAllListeners('token_deleted');
      socketService.removeAllListeners('tokens_deleted');
      socketService.removeAllListeners('participant_joined');
      socketService.removeAllListeners('participant_left');
      socketService.removeAllListeners('participants_updated');
      socketService.removeAllListeners('barrier_created');
      socketService.removeAllListeners('barrier_deleted');
      socketService.removeAllListeners('barrier_updated');
      socketService.removeAllListeners('fog_revealed');
      socketService.removeAllListeners('fog_hidden');
      socketService.removeAllListeners('map_vision_updated');
      socketService.removeAllListeners('chat_history');
      socketService.removeAllListeners('chat_message_sent');
      socketService.removeAllListeners('dice_rolled');
      socketService.removeAllListeners('map_switched'); // Phase 6
      socketService.removeAllListeners('map_created'); // Phase 6
      socketService.removeAllListeners('map_updated'); // Phase 6
      socketService.removeAllListeners('map_deleted'); // Phase 6
      socketService.removeAllListeners('error');
      socketService.removeAllListeners('pong');
      socketService.removeAllListeners('initiative_updated');
      socketService.removeAllListeners('initiative_cleared');
      socketService.removeAllListeners('role_changed');
      socketService.removeAllListeners('gm_handoff_offered');
      socketService.removeAllListeners('gm_handoff_expired');
      socketService.removeAllListeners('gm_handoff_declined');
      socketService.removeAllListeners('light_source_created');
      socketService.removeAllListeners('light_source_updated');
      socketService.removeAllListeners('light_source_deleted');
      socketService.disconnect();

      onDisconnected?.();
    };
  }, [token, sessionId, currentUserId, role, addBarrier, removeBarrier, setBarriers, replaceBarrier, updateBarrier, setLosEnabled, setShowBarriers, setRenderMode, setFog, revealFogCells, hideFogCells, addMessage, setMessages, setMaps, setActiveMapId, addMap, updateMap, removeMap, setInitiative, clearInitiativeStore, setGmHandoffOffer, upsertRemoteRuler, clearRemoteRuler, addPing, setLightSources, addLightSource, removeLightSource, updateLightSourceInStore, setLightingEnabled, onRoleChanged, onCharacterNeedsRefresh]);

  return status;
}
